import { getExtension, isResource } from '@medplum/core'
import type {
  Extension as FHIRExtension,
  HealthcareService,
  Organization,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeFromCodeableConcept,
  conceptMapFromCodes,
  displayFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  HEALTHCARE_SERVICE_CATEGORY,
  HEALTHCARE_SERVICE_TYPE,
} from '#src/codeSystems.ts'
import { Extension } from '#src/decorators.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { schedulableRoles } from '#src/models/care-teams/constants.ts'
import type { SchedulableRole } from '#src/models/care-teams/index.ts'
import { Role } from '#src/roles.ts'
import type { QueryTypes } from '#src/search.ts'
import { AVERAGE_DURATION } from '#src/structureDefinitions.ts'
import { build, isOfType } from '#src/utils.ts'

export class ExtendedHealthcareService extends ExtendedResource<HealthcareService> {
  static readonly RESOURCE_TYPE = 'HealthcareService'

  static isInstance(value: unknown): value is HealthcareService {
    return isResource(value) && isOfType(value, 'HealthcareService')
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return {
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  static querySpecialistType(): QueryTypes {
    return {
      'service-type': SPECIALIST_VISIT_HEALTHCARE_SERVICE.type,
      'service-category': SPECIALIST_VISIT_HEALTHCARE_SERVICE.category,
    }
  }

  static queryLabDraws(): QueryTypes {
    return {
      'service-type': SERVICE_TYPES.CLINIC_PROCEDURE,
      'service-category': SERVICE_CATEGORIES.LAB_DRAWS,
    }
  }

  get name(): string {
    if (!this.resource.name) {
      console.error(this.reference.reference, 'has no name')
    }
    return this.resource.name ?? ''
  }

  // Absent on some services (shared/global catalog entries) — callers
  // org-scoping a service list should treat an absent providedBy as
  // available to every org, not as belonging to none.
  get providedBy(): Reference<Organization> | undefined {
    return this.resource.providedBy
  }

  @Extension({
    url: AVERAGE_DURATION,
    kind: 'valueQuantity',
  })
  averageDuration?: FHIRExtension['valueQuantity']

  get averageDurationMinutes(): number {
    return this.averageDuration?.value ?? 30
  }

  get searchKeywords(): string[] {
    return [this.name]
  }

  get type(): HealthcareServiceType | undefined {
    return codeFromCodeableConcept(
      this.resource.type,
      HEALTHCARE_SERVICE_TYPE
    ) as HealthcareServiceType | undefined
  }

  get category(): HealthcareServiceCategory | undefined {
    return codeFromCodeableConcept(
      this.resource.category,
      HEALTHCARE_SERVICE_CATEGORY
    ) as HealthcareServiceCategory | undefined
  }

  get isDayCenter(): boolean {
    return this.type === 'day-center'
  }

  get isExternal(): boolean {
    return (
      this.type === 'office-visit' && this.category === 'clinical-specialists'
    )
  }

  get isMOE(): boolean {
    return this.type === 'pre-enrollment-visit'
  }

  get isClinic(): boolean {
    return (
      ((this.type === 'office-visit' &&
        this.category !== 'clinical-specialists') ||
        this.type === 'clinic-pcp' ||
        this.type === 'clinic-procedure' ||
        this.type === 'clinic-medication') &&
      !this.isRehab
    )
  }

  get isRehab(): boolean {
    return (
      !!this.resource.category
        ?.flatMap((c) => c.coding)
        ?.some((c) => c?.code === 'rehabilitation') ||
      this.type === 'rehabilitation' ||
      (this.type === 'office-visit' &&
        (this.category === 'ot' || this.category === 'pt'))
    )
  }

  get isAssessment(): boolean {
    return this.type === 'assessment'
  }

  get isDelivery(): boolean {
    return this.type === 'delivery'
  }

  get isPhoneCall(): boolean {
    return this.category === 'phone-call'
  }
}

// RUM appointment-kind qualifier: `<type>[.<category>]`, always derived from
// the controlled type/category codes above, never from `display` — `display`
// is generated from those same codes, so slugifying it back would be a
// fragile round-trip through free text.
export function buildAppointmentKind(
  healthcareService: ExtendedHealthcareService | undefined
): string {
  const type = healthcareService?.type
  if (!type) {
    return 'unknown'
  }
  const category = healthcareService?.category
  return category ? `${type}.${category}` : type
}

export const getAverageDurationMinutes = (hs: HealthcareService): number => {
  const averageDurationExt = getExtension(hs, AVERAGE_DURATION)
  return averageDurationExt?.valueQuantity?.value ?? 30
}
export const buildAverageDurationMinutes = (
  minutes: number
): FHIRExtension => ({
  url: AVERAGE_DURATION,
  valueQuantity: {
    value: minutes,
    system: 'http://unitsofmeasure.org',
    unit: 'min',
  },
})

const serviceTypes = [
  'day-center',
  'walk-in',
  'pre-enrollment-visit',
  'assessment',
  'office-visit',
  'clinic-pcp',
  'clinic-procedure',
  'clinic-medication',
  'social-work-support',
  'follow-up-non-clinical',
  'rehabilitation',
  'home-care',
  'referral',
  'delivery',
  'disenrollment',
  'adl-iadl-support',
] as const

type HealthcareServiceType = (typeof serviceTypes)[number]

const schedulableRoleCodesLower = schedulableRoles.map((code) =>
  code.toLowerCase()
) as Lowercase<SchedulableRole>[]

const serviceCategories = [
  'urgent-care',
  'recreational-activity',
  'lunch',
  'pre-enrollment-assessment',
  'pre-enrollment-education',
  'enrollment-meeting',
  'outreach-visit',
  'center-tour',
  'initial',
  '180-d',
  'change-of-care',
  'sdr',
  ...schedulableRoleCodesLower,
  'routine-checkup',
  'transitions-of-care-follow-up',
  'falls-follow-up',
  'wound-care-follow-up',
  'lab-draws',
  'immunizations',
  'wound-care',
  'medication-administration',
  'risk-stratification',
  'case-management',
  'education',
  'wellness-checks',
  'phone-call',
  'email-message',
  'in-person',
  'assessment',
  'routine',
  'follow-up',
  'clinical-specialists',
  'home-visit',
  'home-health',
  'home-delivered-meals',
  // TODO: possibility to explicitly schedule transportation appointments
  // 'home-to-center',
  // 'center-to-home',
  // 'home-to-referral',
  // 'referral-to-home',
  // 'center-to-referral',
  // 'referral-to-center',
  // 'other',
  'dme-setup',
  'ongoing-supplies-delivery',
  'dme-delivery',
  'disenrollment-retention-conference',
  'disenrollment-meeting',
  'massage-therapy',
  'speech-therapy',
  'weight-followup',
  'rd-follow-up',
  'rd-consult',
  'bathing',
  'laundry',
  'other-delivery',
] as const

type HealthcareServiceCategory = (typeof serviceCategories)[number]

export const SERVICE_TYPES = conceptMapFromCodes({
  codes: serviceTypes,
  system: HEALTHCARE_SERVICE_TYPE,
})

export const SERVICE_CATEGORIES = conceptMapFromCodes({
  codes: serviceCategories,
  system: HEALTHCARE_SERVICE_CATEGORY,
})

type RoleAndHealthcareServicePartial = {
  healthcareServicePartial: Partial<HealthcareService>
  roles: SchedulableRole[]
}

export const SPECIALIST_VISIT_HEALTHCARE_SERVICE: HealthcareService = build(
  'HealthcareService',
  {
    name: `${displayFromCodeableConcept(SERVICE_CATEGORIES.CLINICAL_SPECIALISTS)} ${displayFromCodeableConcept(SERVICE_TYPES.OFFICE_VISIT)}`,
    type: [SERVICE_TYPES.OFFICE_VISIT],
    category: [SERVICE_CATEGORIES.CLINICAL_SPECIALISTS],
    extension: [buildAverageDurationMinutes(60)],
  }
)

export const rolesAndHealthcareServicePartials: RoleAndHealthcareServicePartial[] =
  [
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.DAY_CENTER],
        category: [],
        extension: [buildAverageDurationMinutes(60 * 4)],
      },
      roles: [Role.CM, Role.RT],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.WALK_IN],
        category: [SERVICE_CATEGORIES.URGENT_CARE],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.PCP, Role.RN],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.WALK_IN],
        category: [SERVICE_CATEGORIES.RECREATIONAL_ACTIVITY],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.PCP, Role.RN],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.WALK_IN],
        category: [SERVICE_CATEGORIES.LUNCH],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.PCP, Role.RN],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.PRE_ENROLLMENT_VISIT],
        category: [SERVICE_CATEGORIES.PRE_ENROLLMENT_ASSESSMENT],
        extension: [buildAverageDurationMinutes(60 * 2)],
      },
      roles: [Role.CM, Role.RT],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.PRE_ENROLLMENT_VISIT],
        category: [SERVICE_CATEGORIES.PRE_ENROLLMENT_EDUCATION],
        extension: [buildAverageDurationMinutes(45)],
      },
      roles: [Role.CM, Role.RT],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.PRE_ENROLLMENT_VISIT],
        category: [SERVICE_CATEGORIES.OUTREACH_VISIT],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.CM, Role.RT, Role.RN],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.PRE_ENROLLMENT_VISIT],
        category: [SERVICE_CATEGORIES.CENTER_TOUR],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.CM, Role.RT, Role.RN],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.ASSESSMENT],
        category: [SERVICE_CATEGORIES.INITIAL],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [
        Role.PCP,
        Role.RN,
        Role.SW,
        Role.PT,
        Role.OT,
        Role.RT,
        Role.RD,
        Role.HC,
        Role.PC,
        Role.MA,
        Role.NP,
        Role.PARTICIPANT_EXPERIENCE_SUPERVISOR,
        Role.CNA,
        Role.PTA,
      ],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.ASSESSMENT],
        category: [SERVICE_CATEGORIES['180_D']],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [
        Role.PCP,
        Role.RN,
        Role.SW,
        Role.PT,
        Role.OT,
        Role.RT,
        Role.RD,
        Role.HC,
        Role.PC,
        Role.MA,
        Role.NP,
        Role.PARTICIPANT_EXPERIENCE_SUPERVISOR,
        Role.CNA,
        Role.PTA,
      ],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.ASSESSMENT],
        category: [SERVICE_CATEGORIES.CHANGE_OF_CARE],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [
        Role.PCP,
        Role.RN,
        Role.SW,
        Role.PT,
        Role.OT,
        Role.RT,
        Role.RD,
        Role.HC,
        Role.PC,
        Role.MA,
        Role.NP,
        Role.PARTICIPANT_EXPERIENCE_SUPERVISOR,
        Role.CNA,
        Role.PTA,
      ],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.ASSESSMENT],
        category: [SERVICE_CATEGORIES.SDR],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [
        Role.PCP,
        Role.RN,
        Role.SW,
        Role.PT,
        Role.OT,
        Role.RT,
        Role.RD,
        Role.HC,
        Role.PC,
        Role.MA,
        Role.NP,
        Role.PARTICIPANT_EXPERIENCE_SUPERVISOR,
        Role.CNA,
        Role.PTA,
      ],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.PCP],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.PCP],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.RN],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.RN],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.LVN],
        extension: [buildAverageDurationMinutes(60)],
        // Explicit name so the acronym seeds as "LVN"; the auto-generated
        // display from the category code would otherwise be "Lvn".
        name: 'Office Visit - LVN',
      },
      roles: [Role.LVN],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.SW],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.SW],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.PT],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.PT],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.OT],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.OT],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.RT],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.RT],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.RD],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.RD],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.RD_FOLLOW_UP],
        extension: [buildAverageDurationMinutes(60)],
        // Explicit name so the acronym seeds as "RD"; the auto-generated
        // display would otherwise be "Office Visit - Rd Follow Up".
        name: 'RD Follow-Up',
      },
      roles: [Role.RD],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.RD_CONSULT],
        extension: [buildAverageDurationMinutes(60)],
        // Explicit name so the acronym seeds as "RD"; the auto-generated
        // display would otherwise be "Office Visit - Rd Consult".
        name: 'RD Consult',
      },
      roles: [Role.RD],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.HC],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.HC],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.PC],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.PC],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.CNA],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.CNA],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.PTA],
        extension: [buildAverageDurationMinutes(60)],
        // Explicit name so the acronym seeds as "PTA"; the auto-generated
        // display from the category code would otherwise be "Pta".
        name: 'Office Visit - PTA',
      },
      roles: [Role.PTA],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.MA],
        extension: [buildAverageDurationMinutes(20)],
      },
      roles: [Role.MA],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.ENROLLMENT_MEETING],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.CM],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.CLINIC_PCP],
        category: [SERVICE_CATEGORIES.ROUTINE_CHECKUP],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.PCP],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.CLINIC_PCP],
        category: [SERVICE_CATEGORIES.TRANSITIONS_OF_CARE_FOLLOW_UP],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.PCP],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.CLINIC_PCP],
        category: [SERVICE_CATEGORIES.FALLS_FOLLOW_UP],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.PCP],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.CLINIC_PCP],
        category: [SERVICE_CATEGORIES.WOUND_CARE_FOLLOW_UP],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.PCP],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.CLINIC_PROCEDURE],
        category: [SERVICE_CATEGORIES.LAB_DRAWS],
        extension: [buildAverageDurationMinutes(30)],
      },
      roles: [Role.RN, Role.PCP],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.CLINIC_PROCEDURE],
        category: [SERVICE_CATEGORIES.IMMUNIZATIONS],
        extension: [buildAverageDurationMinutes(30)],
      },
      roles: [Role.RN, Role.PCP],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.CLINIC_PROCEDURE],
        category: [SERVICE_CATEGORIES.WOUND_CARE],
        extension: [buildAverageDurationMinutes(30)],
      },
      roles: [Role.RN, Role.PCP],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.CLINIC_MEDICATION],
        category: [SERVICE_CATEGORIES.MEDICATION_ADMINISTRATION],
        extension: [buildAverageDurationMinutes(30)],
      },
      roles: [Role.RN, Role.PCP],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.SOCIAL_WORK_SUPPORT],
        category: [SERVICE_CATEGORIES.RISK_STRATIFICATION],
        extension: [buildAverageDurationMinutes(45)],
      },
      roles: [Role.SW],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.SOCIAL_WORK_SUPPORT],
        category: [SERVICE_CATEGORIES.CASE_MANAGEMENT],
        extension: [buildAverageDurationMinutes(45)],
      },
      roles: [Role.SW],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.SOCIAL_WORK_SUPPORT],
        category: [SERVICE_CATEGORIES.EDUCATION],
        extension: [buildAverageDurationMinutes(45)],
      },
      roles: [Role.SW],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.SOCIAL_WORK_SUPPORT],
        category: [SERVICE_CATEGORIES.WELLNESS_CHECKS],
        extension: [buildAverageDurationMinutes(45)],
      },
      roles: [Role.SW],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.DISENROLLMENT],
        category: [SERVICE_CATEGORIES.DISENROLLMENT_RETENTION_CONFERENCE],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.CM, Role.RT],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.DISENROLLMENT],
        category: [SERVICE_CATEGORIES.DISENROLLMENT_MEETING],
        extension: [buildAverageDurationMinutes(60)],
      },
      roles: [Role.CM, Role.RT],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.HOME_CARE],
        category: [SERVICE_CATEGORIES.HOME_VISIT],
        extension: [buildAverageDurationMinutes(60)],
        name: 'Home Visit - general',
      },
      roles: [
        Role.OT,
        Role.PT,
        Role.CM,
        Role.PARTICIPANT_EXPERIENCE_SUPERVISOR,
        Role.HC,
      ],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.HOME_CARE],
        category: [SERVICE_CATEGORIES.INITIAL],
        extension: [buildAverageDurationMinutes(60)],
        name: 'HCC (Home Care) Eval',
      },
      roles: [
        Role.OT,
        Role.PT,
        Role.CM,
        Role.PARTICIPANT_EXPERIENCE_SUPERVISOR,
        Role.HC,
      ],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.REHABILITATION],
        category: [SERVICE_CATEGORIES.MASSAGE_THERAPY],
        extension: [buildAverageDurationMinutes(60)],
        name: 'Massage Therapy',
      },
      roles: [Role.PT],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.REHABILITATION],
        category: [SERVICE_CATEGORIES.SPEECH_THERAPY],
        extension: [buildAverageDurationMinutes(45)],
        name: 'Speech Therapy',
      },
      roles: [Role.PT],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.CLINIC_PROCEDURE],
        category: [SERVICE_CATEGORIES.WEIGHT_FOLLOWUP],
        extension: [buildAverageDurationMinutes(30)],
        name: 'Weight Followup',
      },
      roles: [Role.RD],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.OFFICE_VISIT],
        category: [SERVICE_CATEGORIES.PHONE_CALL],
        extension: [buildAverageDurationMinutes(10)],
        name: 'Phone call for care management',
      },
      roles: [Role.CM, Role.RN, Role.SW, Role.RD],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.DELIVERY],
        category: [SERVICE_CATEGORIES.HOME_DELIVERED_MEALS],
        extension: [buildAverageDurationMinutes(90)],
        name: 'Home delivery-Meals/supplements',
      },
      roles: [],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.DELIVERY],
        category: [SERVICE_CATEGORIES.OTHER_DELIVERY],
        extension: [buildAverageDurationMinutes(30)],
        name: 'Other Delivery',
      },
      roles: [],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.ADL_IADL_SUPPORT],
        category: [SERVICE_CATEGORIES.BATHING],
        extension: [buildAverageDurationMinutes(30)],
        name: 'Bathing',
      },
      roles: [Role.OT, Role.PC, Role.CNA],
    },
    {
      healthcareServicePartial: {
        type: [SERVICE_TYPES.ADL_IADL_SUPPORT],
        category: [SERVICE_CATEGORIES.LAUNDRY],
        extension: [buildAverageDurationMinutes(30)],
        name: 'Laundry',
      },
      roles: [Role.OT, Role.PC, Role.CNA],
    },
  ]

/**
 * The role(s) that cover a service category, looked up in the canonical
 * `rolesAndHealthcareServicePartials` catalog — the single source of truth
 * for which roles staff each service line. Returns `[]` for unknown or
 * absent categories.
 *
 * @param code - A `HEALTHCARE_SERVICE_CATEGORY` code, e.g. `massage-therapy`
 * @returns The schedulable roles that cover the category
 */
export function rolesForServiceCategory(
  code: string | null | undefined
): SchedulableRole[] {
  if (!code) {
    return []
  }
  const roles = new Set<SchedulableRole>()
  for (const entry of rolesAndHealthcareServicePartials) {
    const matches = entry.healthcareServicePartial.category
      ?.flatMap((concept) => concept.coding ?? [])
      .some(
        (coding) =>
          coding.system === HEALTHCARE_SERVICE_CATEGORY && coding.code === code
      )
    if (matches) {
      for (const role of entry.roles) {
        roles.add(role)
      }
    }
  }
  return [...roles]
}

/**
 * The role(s) that staff one specific service line, narrowed by BOTH type and
 * category. A category alone is not unique — `initial` covers both the
 * full-IDT Assessment visit and the HCC (Home Care) Eval, whose rosters
 * differ — so a caller reasoning about a single selected service must match
 * on the type too or it inherits roles from an unrelated service line.
 *
 * Falls back to category-only matching when no type is given.
 *
 * Some service lines are identified by type alone and carry no category at
 * all — Day Center is catalogued as `category: []`. For those the type is
 * already unique, so a caller passing no category still gets the roster
 * rather than an empty roster.
 *
 * @param service - The selected service's type and category codes
 * @param service.type - A `HEALTHCARE_SERVICE_TYPE` code, e.g. `home-care`
 * @param service.category - A `HEALTHCARE_SERVICE_CATEGORY` code, e.g. `initial`
 * @returns The schedulable roles that staff that service line
 */
export function rolesForService({
  type,
  category,
}: {
  type?: string | null
  category?: string | null
}): SchedulableRole[] {
  if (!type) {
    return rolesForServiceCategory(category)
  }
  const roles = new Set<SchedulableRole>()
  for (const entry of rolesAndHealthcareServicePartials) {
    const { category: entryCategory, type: entryType } =
      entry.healthcareServicePartial
    const entryCategoryCodes =
      entryCategory?.flatMap((concept) => concept.coding ?? []) ?? []
    const categoryMatches = category
      ? entryCategoryCodes.some(
          (coding) =>
            coding.system === HEALTHCARE_SERVICE_CATEGORY &&
            coding.code === category
        )
      : // No category to match on: only a category-less service line can be
        // identified by its type alone. A caller that omits the category of a
        // service that HAS one must not inherit an unrelated line's roster.
        entryCategoryCodes.length === 0
    const typeMatches = entryType
      ?.flatMap((concept) => concept.coding ?? [])
      .some(
        (coding) =>
          coding.system === HEALTHCARE_SERVICE_TYPE && coding.code === type
      )
    if (categoryMatches && typeMatches) {
      for (const role of entry.roles) {
        roles.add(role)
      }
    }
  }
  return [...roles]
}
